<!-- src/App.vue -->
<template>
  <!-- 브라우저에서는 PWA 설치 화면 표시 -->
  <InstallPWA v-if="!isPWA && !isDebug" />
  
  <!-- PWA 모드에서는 기존 앱 표시 -->
  <template v-else>
    <SplashScreen v-if="showSplash" />
    <div v-else-if="showRouterView" class="app-viewport">
      <RouterView />
    </div>
    
    <!-- 전역 Alert Dialog -->
    <AlertDialog
      :model-value="dialogStore.isVisible"
      :title="dialogStore.title"
      :message="dialogStore.message"
      :confirm-text="dialogStore.confirmText"
      :cancel-text="dialogStore.cancelText"
      :show-cancel-button="dialogStore.showCancel"
      :close-on-overlay-click="false"
      @update:model-value="handleDialogUpdate"
      @confirm="handleDialogConfirm"
      @cancel="handleDialogCancel"
      @neutral="handleDialogNeutral"
      :neutral-text="dialogStore.neutralText"
      :show-neutral-button="dialogStore.showNeutral"
    />
  </template>
</template>

<script>
import SplashScreen from './components/SplashScreen.vue'
import InstallPWA from './components/InstallPWA.vue'
import AlertDialog from './components/AlertDialog.vue'
import useDialogStore from '@common/js/stores/dialog'

export default {
  name: 'App',
  components: {
    SplashScreen,
    InstallPWA,
    AlertDialog
  },
  setup() {
    const dialogStore = useDialogStore()
    return { dialogStore }
  },
  data() {
    return {
      isPWA: false,
      isDebug: false,
      isIOS: false,
      showSplash: false,
      showRouterView: false,
      splashTimerId: null
    }
  },
  created() {
    // 디버그 모드 확인
    try {
      this.isDebug = __IS_DEBUG__
    } catch (e) {
      this.isDebug = false
    }

    // PWA로 설치되어 실행 중인지 확인
    this.checkPWAMode()

    // 스플래시가 이미 표시되었는지 확인 (sessionStorage 사용)
    // sessionStorage를 사용하면 SW 업데이트로 인한 새로고침 시에도 스플래시가 한 번만 표시됨
    // 세션이 종료되면 초기화되므로 앱을 완전히 다시 열면 스플래시가 표시됨
    const splashShown = sessionStorage.getItem('splashShown') === 'true' || window.__SPLASH_SHOWN__
    
    if (splashShown) {
      console.log('Splash already shown in this session, skipping...')
      this.showSplash = false
      this.showRouterView = true
    } else if (this.isPWA || this.isIOS || this.isDebug) {
      // 스플래시를 표시해야 하는 경우
      this.showSplash = true
      this.showRouterView = false
      // 세션 저장소와 전역 플래그 모두 설정
      sessionStorage.setItem('splashShown', 'true')
      window.__SPLASH_SHOWN__ = true
    }
  },
  mounted() {
    // PWA 모드일 때만 스플래시 화면 표시
    if (this.showSplash && (this.isPWA || this.isIOS || this.isDebug)) {
      // 기존 타이머가 있으면 제거 (중복 실행 방지)
      if (this.splashTimerId) {
        clearTimeout(this.splashTimerId)
      }
      
      // 스플래시 화면을 2초 동안 표시
      this.splashTimerId = setTimeout(() => {
        this.showSplash = false
        this.showRouterView = true
        this.splashTimerId = null
      }, 2000)
    }
  },
  beforeUnmount() {
    // 컴포넌트 언마운트 시 타이머 정리
    if (this.splashTimerId) {
      clearTimeout(this.splashTimerId)
      this.splashTimerId = null
    }
  },
  methods: {
    checkPWAMode() {
      // iOS 기기인지 확인
      const userAgent = navigator.userAgent
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) ||
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      this.isIOS = isIOS

      // PWA로 설치되어 실행 중인지 확인하는 여러 방법
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIOSStandalone = window.navigator.standalone === true
      const isInWebApk = document.referrer.includes('android-app://')

      this.isPWA = isStandalone || isIOSStandalone || isInWebApk

      console.log('PWA Mode:', this.isPWA)
      console.log('Standalone:', isStandalone)
      console.log('iOS Standalone:', isIOSStandalone)
      console.log('isIOS:', this.isIOS)
    },
    handleDialogConfirm() {
      if (this.dialogStore.promiseResolve) {
        this.dialogStore.promiseResolve(true)
      }
      this.dialogStore.hideDialog()
    },
    handleDialogCancel() {
      if (this.dialogStore.promiseResolve) {
        this.dialogStore.promiseResolve(false)
      }
      this.dialogStore.hideDialog()
    },
    handleDialogNeutral() {
      if (this.dialogStore.promiseResolve) {
        this.dialogStore.promiseResolve('neutral')
      }
      this.dialogStore.hideDialog()
    },
    handleDialogUpdate(val) {
      if (!val && this.dialogStore.isVisible) {
        // Overlay click or internal close
        if (this.dialogStore.promiseResolve) {
          this.dialogStore.promiseResolve(false)
        }
        this.dialogStore.hideDialog()
      }
    }
  },
  watch: {
    showRouterView(newVal) {
      const app = document.getElementById('app')
      if (!newVal && this.isPWA) {
        app.style.backgroundColor = '#007680'
      } else {
        app.style.backgroundColor = ''
      }
    }
  }
}
</script>
